import React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ArrowUp, ArrowDown, Trash, Copy } from 'react-feather';
import Button from '@/components/styled/Button';
import { WithExplicitFontType } from '@/components/WithExplicitFontType';
import { useTranslation } from 'react-i18next';
import FormatIcon from '@/components/styled/FormatIcon';
import { ColorMenuItem } from './ColorMenuItem';

interface TableRowMenuProps {
    rowIndex: number;
    totalRows: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onDelete: () => void;
    onInsertAbove: () => void;
    onInsertBelow: () => void;
    onSetBackgroundColor?: (color: string) => void;
    onDuplicate: () => void;
}

export function TableRowMenu({
    rowIndex,
    totalRows,
    onMoveUp,
    onMoveDown,
    onDelete,
    onInsertAbove,
    onInsertBelow,
    onSetBackgroundColor,
    onDuplicate,
}: TableRowMenuProps) {
    const { t } = useTranslation();
    const canMoveUp = rowIndex > 0;
    const canMoveDown = rowIndex < totalRows - 1;

    return (
        <Popover.Content
            onOpenAutoFocus={(e) => e.preventDefault()}
            side="bottom"
            className="z-[1000]"
            sideOffset={5}
            align="start"
            onPointerDownOutside={(e) => e.preventDefault()}
        >
            <WithExplicitFontType type="interface">
                <div
                    className="p-1.5 overflow-hidden relative shadow-popupSmall max-w-highlight rounded-md bg-white dark:bg-saga-gray-900 dark:border dark:border-saga-gray-800 pointer-events-auto"
                    contentEditable={false}
                >
                    <div className="flex flex-col w-full">
                        {totalRows > 1 && canMoveUp && (
                            <Button.Plain onClick={onMoveUp}>
                                <Button.BasePadding>
                                    <span className="flex text-sm">
                                        <ArrowUp className="stroke-gray-dark mr-2 my-auto" size={14} />
                                        <span>{t('table.move_up')}</span>
                                    </span>
                                </Button.BasePadding>
                            </Button.Plain>
                        )}
                        {totalRows > 1 && canMoveDown && (
                            <Button.Plain onClick={onMoveDown}>
                                <Button.BasePadding>
                                    <span className="flex text-sm">
                                        <ArrowDown className="stroke-gray-dark mr-2 my-auto" size={14} />
                                        <span>{t('table.move_down')}</span>
                                    </span>
                                </Button.BasePadding>
                            </Button.Plain>
                        )}
                        {totalRows > 1 && (canMoveUp || canMoveDown) && (
                            <div className="border-t dark:border-saga-gray-700 my-1" />
                        )}
                        <Button.Plain onClick={onInsertAbove}>
                            <Button.BasePadding>
                                <span className="flex text-sm items-center">
                                    <span className="mr-2 -mb-0.5">
                                        <FormatIcon type="table" />
                                    </span>
                                    <span>{t('table.insert_above')}</span>
                                </span>
                            </Button.BasePadding>
                        </Button.Plain>
                        <Button.Plain onClick={onInsertBelow}>
                            <Button.BasePadding>
                                <span className="flex text-sm items-center">
                                    <span className="mr-2 -mb-0.5">
                                        <FormatIcon type="table" />
                                    </span>
                                    <span>{t('table.insert_below')}</span>
                                </span>
                            </Button.BasePadding>
                        </Button.Plain>
                        {onSetBackgroundColor && (
                            <ColorMenuItem
                                onSetBackgroundColor={onSetBackgroundColor}
                                zIndex={1001}
                                label={t('table.set_color')}
                            />
                        )}
                        <div className="border-t dark:border-saga-gray-700 my-1" />
                        <Button.Plain onClick={onDuplicate}>
                            <Button.BasePadding>
                                <span className="flex text-sm">
                                    <Copy className="stroke-gray-dark mr-2 my-auto" size={14} />
                                    <span>{t('common.duplicate')}</span>
                                </span>
                            </Button.BasePadding>
                        </Button.Plain>
                        <div className="border-t dark:border-saga-gray-700 my-1" />
                        <Button.Plain onClick={onDelete}>
                            <Button.BasePadding>
                                <span className="flex text-sm">
                                    <Trash className="stroke-gray-dark mr-2 my-auto" size={14} />
                                    <span>{t('common.delete')}</span>
                                </span>
                            </Button.BasePadding>
                        </Button.Plain>
                    </div>
                </div>
            </WithExplicitFontType>
        </Popover.Content>
    );
}
